/**
 * ProviderDashboard
 * Merchant analytics dashboard with KPI cards, revenue chart (CSS bars),
 * recent bookings table, and quick-action links.
 * Glassmorphism UI with backdrop-filter blur and semi-transparent surfaces.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getMerchantDashboard,
  getMerchantRevenue,
  getRecentBookings,
} from '../services/api';

// ---------------------------------------------------------------------------
// Shared glassmorphism tokens
// ---------------------------------------------------------------------------
const glass = {
  card: {
    background: 'rgba(255,255,255,0.08)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '16px',
    padding: '24px',
  },
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
    padding: '32px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#e2e8f0',
  },
};

// ---------------------------------------------------------------------------
// Skeleton placeholder
// ---------------------------------------------------------------------------
function Skeleton({ width = '100%', height = '20px' }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: '8px',
        background: 'linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------
function KPICard({ label, value, suffix, loading }) {
  return (
    <div style={{ ...glass.card, flex: '1 1 200px', minWidth: '180px', textAlign: 'center' }}>
      {loading ? (
        <>
          <Skeleton width="60%" height="32px" />
          <div style={{ height: '8px' }} />
          <Skeleton width="80%" height="14px" />
        </>
      ) : (
        <>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#f1f5f9', marginBottom: '4px' }}>
            {value}{suffix || ''}
          </div>
          <div style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '500' }}>{label}</div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Revenue Bar Chart (pure CSS)
// ---------------------------------------------------------------------------
function RevenueChart({ data, loading }) {
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', height: '200px' }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} width="48px" height={`${40 + Math.random() * 100}px`} />
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <div style={{ color: '#94a3b8', textAlign: 'center', padding: '40px 0' }}>No revenue data available.</div>;
  }

  const maxRevenue = Math.max(...data.map((d) => d.revenue || 0), 1);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', height: '220px', padding: '0 8px' }}>
      {data.map((d, i) => {
        const pct = ((d.revenue || 0) / maxRevenue) * 100;
        const label = d.period
          ? new Date(d.period).toLocaleDateString('en-US', { month: 'short' })
          : `M${i + 1}`;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
            <div
              style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', fontWeight: '600' }}
              title={`$${((d.revenue || 0) / 100).toFixed(2)}`}
            >
              ${((d.revenue || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
            <div
              style={{
                width: '100%',
                maxWidth: '56px',
                height: `${Math.max(pct, 4)}%`,
                background: 'linear-gradient(180deg, #818cf8 0%, #6366f1 100%)',
                borderRadius: '8px 8px 4px 4px',
                transition: 'height 0.4s ease',
              }}
            />
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>{label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recent Bookings Table
// ---------------------------------------------------------------------------
function RecentBookingsTable({ bookings, loading }) {
  const thStyle = {
    textAlign: 'left',
    padding: '10px 14px',
    color: '#94a3b8',
    fontWeight: '600',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  };
  const tdStyle = {
    padding: '12px 14px',
    fontSize: '13px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  };

  if (loading) {
    return (
      <div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} style={{ display: 'flex', gap: '12px', padding: '12px 0' }}>
            <Skeleton width="25%" height="16px" />
            <Skeleton width="20%" height="16px" />
            <Skeleton width="15%" height="16px" />
            <Skeleton width="20%" height="16px" />
          </div>
        ))}
      </div>
    );
  }

  if (!bookings || bookings.length === 0) {
    return <div style={{ color: '#94a3b8', textAlign: 'center', padding: '32px 0' }}>No recent bookings.</div>;
  }

  const statusColors = {
    completed: { bg: 'rgba(16,185,129,0.15)', color: '#6ee7b7' },
    pending: { bg: 'rgba(251,191,36,0.15)', color: '#fbbf24' },
    cancelled: { bg: 'rgba(239,68,68,0.15)', color: '#fca5a5' },
    confirmed: { bg: 'rgba(99,102,241,0.15)', color: '#a5b4fc' },
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thStyle}>Customer</th>
            <th style={thStyle}>Service</th>
            <th style={thStyle}>Amount</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Date</th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((b, i) => {
            const sc = statusColors[b.status] || statusColors.pending;
            return (
              <tr key={b.id || i}>
                <td style={{ ...tdStyle, color: '#f1f5f9', fontWeight: '500' }}>
                  {b.customer_name || b.customerName || `Customer #${b.customer_id || b.id}`}
                </td>
                <td style={{ ...tdStyle, color: '#cbd5e1' }}>
                  {b.service_name || b.serviceName || '-'}
                </td>
                <td style={{ ...tdStyle, color: '#cbd5e1' }}>
                  {b.amount_cents != null ? `$${(b.amount_cents / 100).toFixed(2)}` : '-'}
                </td>
                <td style={tdStyle}>
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: '600',
                      backgroundColor: sc.bg,
                      color: sc.color,
                      textTransform: 'capitalize',
                    }}
                  >
                    {b.status || 'unknown'}
                  </span>
                </td>
                <td style={{ ...tdStyle, color: '#94a3b8' }}>
                  {b.created_at ? new Date(b.created_at).toLocaleDateString() : '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick Actions
// ---------------------------------------------------------------------------
function QuickActions() {
  const actions = [
    { label: 'Manage Availability', to: '/merchant-availability', icon: '\u{1F4C5}' },
    { label: 'Create Promotion', to: '/merchant-promotions', icon: '\u{1F3AF}' },
    { label: 'View Reviews', to: '/reviews', icon: '\u{2B50}' },
  ];

  return (
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
      {actions.map((a) => (
        <Link
          key={a.to}
          to={a.to}
          style={{
            ...glass.card,
            flex: '1 1 160px',
            padding: '16px 20px',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
          onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
          onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
        >
          <span style={{ fontSize: '22px' }}>{a.icon}</span>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>{a.label}</span>
        </Link>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error banner
// ---------------------------------------------------------------------------
function ErrorBanner({ message, onRetry }) {
  return (
    <div
      style={{
        ...glass.card,
        background: 'rgba(239,68,68,0.12)',
        border: '1px solid rgba(239,68,68,0.25)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
      }}
    >
      <span style={{ color: '#fca5a5', fontSize: '14px' }}>{message}</span>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            padding: '6px 16px',
            borderRadius: '8px',
            border: '1px solid rgba(239,68,68,0.3)',
            background: 'rgba(239,68,68,0.15)',
            color: '#fca5a5',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
          }}
        >
          Retry
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Embed Badge Section
// ---------------------------------------------------------------------------
const API_BASE = import.meta.env.VITE_API_URL || '/api';
const APP_URL = import.meta.env.VITE_APP_URL || 'https://sm-compare.com';

function CopyField({ label, code }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: '#f1f5f9' }}>{label}</span>
        <button
          onClick={handleCopy}
          style={{
            background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#e2e8f0', cursor: 'pointer',
          }}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre style={{
        background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '10px 12px',
        fontSize: '12px', color: '#cbd5e1', overflowX: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
      }}>{code}</pre>
    </div>
  );
}

function EmbedBadgeSection({ providerId, providerName }) {
  if (!providerId) return null;
  const badgeUrl = `${API_BASE}/trust-engine/badge/${providerId}.svg`;
  const embedScriptUrl = `${API_BASE}/trust-engine/badge/${providerId}/embed.js`;
  const imgSnippet = `<a href="${APP_URL}/provider/${providerId}" target="_blank" rel="noopener noreferrer">\n  <img src="${badgeUrl}" alt="${providerName || 'Trust Score'}" />\n</a>`;
  const scriptSnippet = `<div id="sm-compare-badge-${providerId}"></div>\n<script src="${embedScriptUrl}" async></script>`;

  return (
    <div style={{ ...glass.card, marginTop: '24px' }}>
      <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#f1f5f9', marginBottom: '4px' }}>
        Get Your Trust Badge
      </h3>
      <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 16px 0' }}>
        Embed your live Trust Score on your own website. Copy either snippet below.
      </p>
      <div style={{ marginBottom: '16px' }}>
        <img src={badgeUrl} alt={providerName || 'Trust Score badge preview'} style={{ borderRadius: '4px' }} />
      </div>
      <CopyField label="Image embed (simplest)" code={imgSnippet} />
      <CopyField label="Script embed (auto-updating)" code={scriptSnippet} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function ProviderDashboard() {
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, revenueData, bookingsData] = await Promise.all([
        getMerchantDashboard(),
        getMerchantRevenue(6),
        getRecentBookings(10),
      ]);
      setStats(statsData);
      setRevenue(revenueData);
      setBookings(Array.isArray(bookingsData) ? bookingsData : bookingsData?.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (cents) => {
    if (cents == null) return '$0';
    return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  return (
    <div style={glass.page}>
      {/* Shimmer keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '26px', fontWeight: '700', color: '#f1f5f9', marginBottom: '4px' }}>
          Merchant Dashboard
        </h1>
        <p style={{ fontSize: '14px', color: '#94a3b8', margin: 0 }}>
          Overview of your business performance and recent activity.
        </p>
      </div>

      {/* Error */}
      {error && <ErrorBanner message={error} onRetry={fetchData} />}

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <KPICard label="Total Bookings" value={stats?.totalBookings ?? 0} loading={loading} />
        <KPICard
          label="Monthly Revenue"
          value={formatCurrency(stats?.monthlyRevenueCents)}
          loading={loading}
        />
        <KPICard label="Avg Rating" value={stats?.avgRating ?? '0'} suffix="/5" loading={loading} />
        <KPICard label="Profile Views" value={stats?.profileViews ?? 0} loading={loading} />
      </div>

      {/* Revenue Chart + Quick Actions */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div style={{ ...glass.card, flex: '2 1 420px', minWidth: '320px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#f1f5f9', marginBottom: '16px' }}>
            Revenue - Last 6 Months
          </h3>
          <RevenueChart data={revenue} loading={loading} />
        </div>

        <div style={{ flex: '1 1 280px', minWidth: '260px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#f1f5f9', margin: '0 0 4px 0' }}>
            Quick Actions
          </h3>
          <QuickActions />
        </div>
      </div>

      {/* Recent Bookings */}
      <div style={{ ...glass.card }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#f1f5f9', margin: 0 }}>
            Recent Bookings
          </h3>
          <span style={{ fontSize: '12px', color: '#94a3b8' }}>Last 10</span>
        </div>
        <RecentBookingsTable bookings={bookings} loading={loading} />
      </div>

      <EmbedBadgeSection providerId={stats?.providerId || stats?.id} providerName={stats?.name || stats?.providerName} />
    </div>
  );
}
